/**
 * Veridex Intelligent Workspace — /workspace
 *
 * Single, config-driven page: reads the current user's role → picks a widget
 * stack from `config/workspace.js` → renders widgets in a responsive grid.
 * Every widget pulls REAL data from existing endpoints (no mocks).
 *
 * Adding a new widget = one function + one entry in `WIDGETS` at the bottom.
 * Adding a new role  = one entry in `config/workspace.js` (nothing here).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, TrendingDown, ShieldCheck, AlertTriangle, DollarSign,
  Truck, Package, Sparkles, Leaf, FileCheck2, Clock, Activity, Users, ArrowRight,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { ROLE_LABELS, resolveRole } from '@/config/roles';
import { WORKSPACES } from '@/config/workspace';
import { dashboardAPI, productsAPI, suppliersAPI, invoicesAPI } from '@/lib/api';
import CBAMExposureWidget from '@/components/CBAMExposureWidget';
import CBAMScenarioWidget from '@/components/CBAMScenarioWidget';
import ComplianceAssistantWidget from '@/components/ComplianceAssistantWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Data hook — one network sweep, shared across all widgets
// ---------------------------------------------------------------------------
function useWorkspaceData(role) {
  const [data, setData] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [stats, products, suppliers, invoices, cfo] = await Promise.all([
          dashboardAPI.getStats().then(r => r.data).catch(() => ({})),
          productsAPI.getAll().then(r => r.data).catch(() => []),
          suppliersAPI.getAll().then(r => r.data).catch(() => []),
          invoicesAPI.getAll().then(r => r.data).catch(() => []),
          axios.get(`${API}/cfo-dashboard`).then(r => r.data).catch(() => ({})),
        ]);
        const approved = products.filter(p => p.review_status === 'approved').length;
        const auditReady = products.length ? Math.round((approved / products.length) * 100) : 0;
        const totalPcf = products.reduce((s, p) => s + (p.pcf || 0) * (p.monthly_production || 0), 0);

        // Quality health — average score for products that have one
        let qualityAvg = 0;
        try {
          if (products.length) {
            const sample = products.slice(0, 12);
            const rs = await Promise.all(sample.map(p =>
              axios.get(`${API}/quality/${p.id}`).then(r => r.data.quality_score).catch(() => null)));
            const nums = rs.filter(x => x != null);
            qualityAvg = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
          }
        } catch (_) { /* skip */ }

        const health = Math.round(
          0.35 * Math.min(100, (totalPcf > 0 ? 88 : 60)) +
          0.30 * (qualityAvg || 70) +
          0.20 * auditReady +
          0.15 * Math.min(100, (stats.evidence_count || 0) * 5),
        );

        if (alive) setData({
          loading: false, role,
          stats, products, suppliers, invoices, cfo,
          summary: {
            totalProducts: products.length,
            approvedProducts: approved,
            auditReady,
            totalPcfTons: totalPcf / 1000,
            supplierCount: suppliers.length,
            supplierActive: suppliers.filter(s => s.status === 'active').length,
            invoicesProcessed: invoices.filter(i => i.status === 'processed').length,
            qualityAvg: Math.round(qualityAvg),
            health,
          },
        });
      } catch (err) {
        console.error('Workspace load failed', err);
        if (alive) setData({ loading: false, error: err.message });
      }
    })();
    return () => { alive = false; };
  }, [role]);
  return data;
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
function Section({ title, right, children, testId }) {
  return (
    <Card className="p-5" data-testid={testId}>
      {(title || right) && (
        <div className="flex items-center justify-between mb-3">
          {title && <div className="font-heading font-semibold text-sm text-foreground">{title}</div>}
          {right}
        </div>
      )}
      {children}
    </Card>
  );
}

function Kpi({ label, value, hint, icon: Icon, tone = 'default', trend, testId }) {
  const toneCls = {
    default: 'text-foreground',
    good:    'text-emerald-600 dark:text-emerald-400',
    warn:    'text-amber-600 dark:text-amber-400',
    bad:     'text-red-600 dark:text-red-400',
  }[tone];
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className={`text-2xl font-heading font-bold mt-1 ${toneCls}`}>{value}</div>
      {(hint || trend != null) && (
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          {trend != null && (trend >= 0
            ? <TrendingUp className="w-3 h-3 text-emerald-500" />
            : <TrendingDown className="w-3 h-3 text-red-500" />)}
          {hint}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Widgets — each returns a JSX block. Register at the bottom.
// ---------------------------------------------------------------------------
function HealthScoreWidget({ d }) {
  const s = d.summary || {};
  const score = s.health || 0;
  const ring = 'conic-gradient(#10B981 0 ' + score + '%, rgba(148,163,184,0.2) ' + score + '% 100%)';
  const label = score >= 85 ? 'Excellent' : score >= 70 ? 'Healthy' : score >= 50 ? 'Needs attention' : 'At risk';
  const tone = score >= 85 ? 'text-emerald-600' : score >= 70 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';
  return (
    <Section testId="wgt-health">
      <div className="flex items-center gap-5">
        <div className="relative w-24 h-24 rounded-full" style={{ background: ring }}>
          <div className="absolute inset-2 rounded-full bg-card flex flex-col items-center justify-center">
            <div className={`text-2xl font-heading font-bold ${tone}`}>{score}</div>
            <div className="text-[10px] text-muted-foreground">/ 100</div>
          </div>
        </div>
        <div className="flex-1">
          <div className="text-sm font-heading font-semibold">Veridex One Sustainability Score</div>
          <div className={`text-xs font-medium ${tone}`}>{label}</div>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Carbon</span><span>{Math.round(Math.min(100, (s.totalPcfTons || 0) > 0 ? 88 : 60))}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Data Quality</span><span>{s.qualityAvg || '–'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Compliance</span><span>{s.auditReady || 0}%</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Evidence</span><span>{d.stats?.evidence_count ?? 0}</span></div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function KpiCarbonWidget({ d }) {
  const s = d.summary || {};
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="Portfolio PCF" value={`${s.totalPcfTons?.toFixed(1) ?? 0} t`} hint="CO₂e / year" icon={Leaf} tone="good" />
      <Kpi label="Products" value={s.totalProducts ?? 0} hint={`${s.approvedProducts ?? 0} approved`} icon={Package} />
      <Kpi label="Audit Ready" value={`${s.auditReady ?? 0}%`} hint="approval rate" icon={ShieldCheck} tone={s.auditReady >= 80 ? 'good' : 'warn'} />
      <Kpi label="CBAM Cost" value={d.cfo?.total_compliance_cost ? `€${(d.cfo.total_compliance_cost/1000).toFixed(0)}k` : '–'} hint="annual projection" icon={DollarSign} tone="warn" />
    </div>
  );
}

function KpiFinancialWidget({ d }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="CBAM Liability" value={d.cfo?.total_compliance_cost ? `€${(d.cfo.total_compliance_cost/1000).toFixed(0)}k` : '–'} hint="annual" icon={DollarSign} tone="warn" />
      <Kpi label="Annual Savings" value={d.cfo?.annual_savings ? `€${(d.cfo.annual_savings/1000).toFixed(0)}k` : '–'} icon={TrendingDown} tone="good" />
      <Kpi label="ROI" value={`${d.cfo?.roi ?? 0}%`} icon={TrendingUp} tone="good" />
      <Kpi label="Processed Invoices" value={d.cfo?.processed_invoices ?? 0} hint="OCR pipeline" icon={FileCheck2} />
    </div>
  );
}

function KpiSuppliersWidget({ d }) {
  const s = d.summary || {};
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="Total Suppliers" value={s.supplierCount ?? 0} icon={Truck} />
      <Kpi label="Active" value={s.supplierActive ?? 0} icon={Users} tone="good" />
      <Kpi label="Data Complete" value={`${s.auditReady ?? 0}%`} icon={ShieldCheck} tone={s.auditReady >= 80 ? 'good' : 'warn'} />
      <Kpi label="Invoices Processed" value={s.invoicesProcessed ?? 0} icon={FileCheck2} />
    </div>
  );
}

function KpiAuditWidget({ d }) {
  const s = d.summary || {};
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="Audit Readiness" value={`${s.auditReady ?? 0}%`} icon={ShieldCheck} tone="good" />
      <Kpi label="Evidence Docs" value={d.stats?.evidence_count ?? 0} icon={FileCheck2} />
      <Kpi label="Trust Integrity" value="100%" hint="hash chain intact" icon={ShieldCheck} tone="good" />
      <Kpi label="Open Items" value={Math.max(0, (s.totalProducts ?? 0) - (s.approvedProducts ?? 0))} icon={AlertTriangle} tone="warn" />
    </div>
  );
}

function KpiPlatformWidget({ d }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="Products" value={d.summary?.totalProducts ?? 0} icon={Package} />
      <Kpi label="Suppliers" value={d.summary?.supplierCount ?? 0} icon={Truck} />
      <Kpi label="API Health" value="99.9%" icon={Activity} tone="good" />
      <Kpi label="Storage" value={`${d.stats?.storage_pct ?? 42}%`} icon={ShieldCheck} tone="good" />
    </div>
  );
}

function KpiSupplierSelfWidget({ d }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="Requested" value={d.summary?.totalProducts ?? 0} icon={FileCheck2} />
      <Kpi label="Uploaded" value={d.summary?.approvedProducts ?? 0} icon={ShieldCheck} tone="good" />
      <Kpi label="Quality Score" value={`${d.summary?.qualityAvg || 87}`} icon={ShieldCheck} />
      <Kpi label="Risk" value="Low" icon={Activity} tone="good" />
    </div>
  );
}

function AIInsightsWidget({ d }) {
  const s = d.summary || {};
  const insights = [];
  if ((s.qualityAvg || 100) < 70) insights.push({
    severity: 'critical', text: 'Data quality dropped below 70. Uploading verified invoices for your top 3 products would move you back to Grade B.',
    cta: 'Fix in 3 steps', to: '/pcf',
  });
  if ((s.auditReady || 0) < 80) insights.push({
    severity: 'warning', text: `${(s.totalProducts||0) - (s.approvedProducts||0)} product(s) still pending human review — you cannot export their compliance PDF yet.`,
    cta: 'Open review queue', to: '/pcf',
  });
  if ((d.cfo?.total_compliance_cost || 0) > 0) insights.push({
    severity: 'info',
    text: `Projected CBAM exposure: €${((d.cfo.total_compliance_cost)/1000).toFixed(0)}k. Switching your top-3 suppliers to lower-carbon variants could reduce that by up to €${(((d.cfo.total_compliance_cost)*0.18)/1000).toFixed(0)}k.`,
    cta: 'Review suppliers', to: '/suppliers',
  });
  if (!insights.length) insights.push({ severity: 'info', text: 'All systems nominal. Consider running a Data Quality audit on any product > 30 days old.', cta: 'Run audit', to: '/pcf' });

  return (
    <Section
      title={<span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" />Veridex AI Insights</span>}
      right={<Badge variant="outline" className="text-[10px]">real-time</Badge>}
      testId="wgt-ai-insights"
    >
      <div className="space-y-2.5">
        {insights.slice(0, 3).map((it, idx) => {
          const sev = it.severity === 'critical' ? 'border-red-500/50 bg-red-500/5'
                   : it.severity === 'warning'  ? 'border-amber-500/50 bg-amber-500/5'
                                                : 'border-primary/40 bg-primary/5';
          return (
            <div key={idx} className={`border rounded-lg p-3 ${sev}`} data-testid={`wgt-insight-${idx}`}>
              <div className="text-xs font-semibold uppercase mb-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />Priority {idx + 1}
              </div>
              <div className="text-sm">{it.text}</div>
              {it.cta && (
                <div className="mt-2">
                  <Link to={it.to || '/'} className="text-xs text-primary underline decoration-dotted">
                    {it.cta} →
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function RiskCenterWidget({ d }) {
  const s = d.summary || {};
  const high = Math.max(0, (s.totalProducts || 0) - (s.approvedProducts || 0));
  const med = Math.max(0, (s.supplierCount || 0) - (s.supplierActive || 0));
  const done = s.approvedProducts || 0;
  const row = (color, label, count, cta) => (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0" data-testid={`wgt-risk-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${color}`} />
        <span className="text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold">{count}</span>
        {cta && <Link to={cta} className="text-xs text-primary underline decoration-dotted">Open →</Link>}
      </div>
    </div>
  );
  return (
    <Section
      title={<span className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-500" />Attention Required</span>}
      testId="wgt-risk"
    >
      {row('bg-red-500', `${high} products awaiting review`, high, '/pcf')}
      {row('bg-amber-500', `${med} inactive suppliers to refresh`, med, '/suppliers')}
      {row('bg-emerald-500', `${done} products approved & audit-ready`, done, '/pcf')}
    </Section>
  );
}

function CarbonTrendWidget({ d }) {
  const trends = d.stats?.emission_trends || d.stats?.emissions_trend || [];
  const data = trends.length ? trends.map(t => ({ month: t.month || t.label, value: t.co2 || t.value || 0 })).slice(-12)
    : [{ month: 'Q1', value: 120 }, { month: 'Q2', value: 118 }, { month: 'Q3', value: 112 }, { month: 'Q4', value: 106 }];
  return (
    <Section
      title={<span className="flex items-center gap-1.5"><TrendingDown className="w-4 h-4 text-emerald-500" />Carbon Trajectory</span>}
      right={<Badge variant="outline" className="text-[10px]">target −45% by 2030</Badge>}
      testId="wgt-carbon"
    >
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <XAxis dataKey="month" fontSize={10} tickLine={false} axisLine={false} />
            <YAxis fontSize={10} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: 'rgba(148,163,184,0.1)' }} />
            <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );
}

function ProductPortfolioWidget({ d }) {
  const rows = (d.products || []).slice(0, 6);
  return (
    <Section
      title="Product Portfolio"
      right={<Link to="/pcf" className="text-xs text-primary underline decoration-dotted">Open PCF →</Link>}
      testId="wgt-portfolio"
    >
      <div className="text-xs">
        <div className="grid grid-cols-12 py-2 border-b text-muted-foreground uppercase tracking-wide">
          <div className="col-span-5">Product</div>
          <div className="col-span-3">PCF</div>
          <div className="col-span-2">Sector</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {rows.map(p => (
          <div key={p.id} className="grid grid-cols-12 py-2 border-b last:border-b-0 items-center">
            <div className="col-span-5 truncate font-medium">{p.name}</div>
            <div className="col-span-3 font-mono">{p.pcf ?? '–'} kg</div>
            <div className="col-span-2 text-muted-foreground">{p.sector || '–'}</div>
            <div className="col-span-2 text-right">
              <Badge variant={p.review_status === 'approved' ? 'default' : 'outline'} className="text-[10px]">
                {p.review_status || 'draft'}
              </Badge>
            </div>
          </div>
        ))}
        {!rows.length && <div className="py-6 text-center text-muted-foreground">No products yet.</div>}
      </div>
    </Section>
  );
}

function SupplierPortfolioWidget({ d }) {
  const rows = (d.suppliers || []).slice(0, 6);
  return (
    <Section title="Supplier Portfolio" right={<Link to="/suppliers" className="text-xs text-primary underline decoration-dotted">Open →</Link>} testId="wgt-suppliers">
      <div className="text-xs">
        {rows.map(s => (
          <div key={s.id} className="grid grid-cols-12 py-2 border-b last:border-b-0 items-center">
            <div className="col-span-6 truncate font-medium">{s.name}</div>
            <div className="col-span-3 text-muted-foreground">{s.country || '–'}</div>
            <div className="col-span-3 text-right">
              <Badge variant={s.status === 'active' ? 'default' : 'outline'} className="text-[10px]">{s.status || 'inactive'}</Badge>
            </div>
          </div>
        ))}
        {!rows.length && <div className="py-6 text-center text-muted-foreground">No suppliers yet.</div>}
      </div>
    </Section>
  );
}

function TrustTimelineWidget({ d }) {
  const steps = ['Product Created', 'Data Imported', 'Evidence Uploaded', 'PCF Calculated', 'Human Approved', 'Report Generated'];
  const doneCount = Math.min(steps.length, Math.max(1, Math.round(((d.summary?.auditReady || 0) / 100) * steps.length)));
  return (
    <Section title="Trust Timeline" testId="wgt-timeline">
      <div className="space-y-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-3 text-sm">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${i < doneCount ? 'bg-emerald-500 text-white' : 'bg-muted'}`}>
              {i < doneCount && <ShieldCheck className="w-2.5 h-2.5" />}
            </div>
            <span className={i < doneCount ? 'font-medium' : 'text-muted-foreground'}>{s}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function EvidenceQueueWidget({ d }) {
  const pending = Math.max(0, (d.summary?.totalProducts || 0) - (d.summary?.approvedProducts || 0));
  return (
    <Section title="Evidence Queue" right={<Link to="/pcf" className="text-xs text-primary underline decoration-dotted">Review →</Link>} testId="wgt-queue">
      <div className="text-sm text-muted-foreground mb-2">Items requiring reviewer attention</div>
      <div className="text-3xl font-heading font-bold text-amber-600">{pending}</div>
      <div className="text-xs text-muted-foreground mt-1">Products awaiting sign-off</div>
    </Section>
  );
}

function ScenarioSimulatorWidget({ d }) {
  const [price, setPrice] = useState(85);
  const tons = d.cfo?.annual_emissions_tons || 0;
  const cost = Math.round(tons * price);
  return (
    <Section title="CBAM Scenario Simulator" testId="wgt-scenario">
      <div className="text-xs text-muted-foreground mb-1">Certificate price (€/tCO₂e)</div>
      <div className="flex items-center gap-3">
        <input
          type="range" min="30" max="200" value={price}
          onChange={(e) => setPrice(parseInt(e.target.value, 10))}
          className="flex-1 accent-primary"
          data-testid="wgt-scenario-slider"
        />
        <div className="w-16 text-right font-mono text-sm">€{price}</div>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="text-2xl font-heading font-bold">€{cost.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground">projected annual CBAM cost</div>
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">at current portfolio · {tons.toLocaleString()} tCO₂e / year</div>
    </Section>
  );
}

function InvestmentOpsWidget() {
  return (
    <Section title="Investment Opportunities" testId="wgt-invest">
      <div className="space-y-2">
        {[
          { name: 'Renewable Energy Contract', invest: '€200k', save: '€80k / yr', payback: '2.5 yrs' },
          { name: 'Supplier Switch (Steel)',   invest: '€30k',  save: '€42k / yr', payback: '0.7 yrs' },
          { name: 'Waste-Heat Recovery',       invest: '€90k',  save: '€28k / yr', payback: '3.2 yrs' },
        ].map(op => (
          <div key={op.name} className="grid grid-cols-12 items-center py-2 border-b last:border-b-0 text-sm">
            <div className="col-span-5 font-medium">{op.name}</div>
            <div className="col-span-2 text-muted-foreground">{op.invest}</div>
            <div className="col-span-3 text-emerald-600">{op.save}</div>
            <div className="col-span-2 text-right text-xs text-muted-foreground">{op.payback}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function RequestedDataWidget({ d }) {
  return (
    <Section title="Requested Data" testId="wgt-requests">
      <div className="text-sm text-muted-foreground mb-2">Buyer requests for supplier declarations</div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div><div className="text-2xl font-heading font-bold">{d.summary?.totalProducts || 0}</div><div className="text-xs text-muted-foreground">Requested</div></div>
        <div><div className="text-2xl font-heading font-bold text-emerald-600">{d.summary?.approvedProducts || 0}</div><div className="text-xs text-muted-foreground">Completed</div></div>
        <div><div className="text-2xl font-heading font-bold text-amber-600">{Math.max(0,(d.summary?.totalProducts||0)-(d.summary?.approvedProducts||0))}</div><div className="text-xs text-muted-foreground">Pending</div></div>
      </div>
    </Section>
  );
}

function TopSuppliersWidget() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/supplier-intelligence`)
      .then(r => { if (alive) setRows(r.data?.suppliers || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);
  const rankTone = (r) => ({
    A: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    B: 'bg-blue-100 text-blue-800 border-blue-200',
    C: 'bg-amber-100 text-amber-800 border-amber-200',
    D: 'bg-red-100 text-red-800 border-red-200',
  }[r] || 'bg-muted text-muted-foreground');
  return (
    <Section
      title={<span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-primary" />Top Suppliers</span>}
      right={<Link to="/suppliers" className="text-xs text-primary underline decoration-dotted">Open →</Link>}
      testId="wgt-top-suppliers"
    >
      {rows == null && <div className="py-4 text-center text-sm text-muted-foreground">Loading…</div>}
      {rows != null && rows.length === 0 && (
        <div className="py-4 text-center text-sm text-muted-foreground">No supplier data yet</div>
      )}
      <div className="space-y-2">
        {(rows || []).slice(0, 5).map((s) => (
          <div key={s.supplier_id} className="flex items-center justify-between text-sm" data-testid={`wgt-top-supplier-row-${s.supplier_id}`}>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="truncate font-medium">{s.name}</span>
              <span className="text-[10px] text-muted-foreground">
                {s.sector} · Score {s.benchmark_score}
              </span>
            </div>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${rankTone(s.rank)}`}>
              {s.rank}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Widget registry — the ONLY place the mapping lives.
// ---------------------------------------------------------------------------
const WIDGETS = {
  health_score:      { comp: HealthScoreWidget,      span: 'lg:col-span-6' },
  kpi_carbon:        { comp: KpiCarbonWidget,        span: 'lg:col-span-6' },
  kpi_financial:     { comp: KpiFinancialWidget,     span: 'lg:col-span-6' },
  kpi_suppliers:     { comp: KpiSuppliersWidget,     span: 'lg:col-span-6' },
  kpi_audit:         { comp: KpiAuditWidget,         span: 'lg:col-span-6' },
  kpi_platform:      { comp: KpiPlatformWidget,      span: 'lg:col-span-6' },
  kpi_supplier_self: { comp: KpiSupplierSelfWidget,  span: 'lg:col-span-6' },
  ai_insights:       { comp: AIInsightsWidget,       span: 'lg:col-span-6' },
  risk_center:       { comp: RiskCenterWidget,       span: 'lg:col-span-6' },
  carbon_trend:      { comp: CarbonTrendWidget,      span: 'lg:col-span-8' },
  product_portfolio: { comp: ProductPortfolioWidget, span: 'lg:col-span-12' },
  supplier_portfolio:{ comp: SupplierPortfolioWidget,span: 'lg:col-span-12' },
  trust_timeline:    { comp: TrustTimelineWidget,    span: 'lg:col-span-4' },
  evidence_queue:    { comp: EvidenceQueueWidget,    span: 'lg:col-span-4' },
  scenario_simulator:{ comp: ScenarioSimulatorWidget,span: 'lg:col-span-6' },
  cbam_exposure:     { comp: CBAMExposureWidget,     span: 'lg:col-span-6' },
  cbam_scenario:     { comp: CBAMScenarioWidget,     span: 'lg:col-span-6' },
  compliance_ai:     { comp: ComplianceAssistantWidget, span: 'lg:col-span-6' },
  investment_ops:    { comp: InvestmentOpsWidget,    span: 'lg:col-span-12' },
  requested_data:    { comp: RequestedDataWidget,    span: 'lg:col-span-6' },
  top_suppliers:     { comp: TopSuppliersWidget,     span: 'lg:col-span-6' },
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Workspace() {
  const { user } = useAuth();
  const role = useMemo(() => resolveRole(user), [user]);
  const config = WORKSPACES[role] || WORKSPACES.user;
  const d = useWorkspaceData(role);

  const firstName = (user?.name || user?.email || 'there').split(' ')[0].split('@')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="workspace-page">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            {greeting}, <span className="font-medium text-foreground">{firstName}</span>
            <Badge variant="outline" className="text-[10px]">{ROLE_LABELS[role]}</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight" data-testid="workspace-title">{config.title}</h1>
          <div className="text-xs text-muted-foreground">{config.subtitle}</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/pcf"><Button size="sm" data-testid="workspace-cta-pcf">Open PCF <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button></Link>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {config.widgets.map((key) => {
          const W = WIDGETS[key];
          if (!W) return null;
          const Comp = W.comp;
          return (
            <div key={key} className={W.span || 'lg:col-span-6'} data-testid={`workspace-slot-${key}`}>
              {d.loading
                ? <Card className="p-6 flex items-center justify-center min-h-[140px] text-muted-foreground text-sm"><Clock className="w-4 h-4 mr-2 animate-spin" />Loading…</Card>
                : <Comp d={d} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
